<template>
  <input-text
    ref="inputElement"
    :value="value"
    :type="fieldType"
    :icons="icons"
    class="input-password"
    :class="{ '-with-strength': strengthVisible }"
    v-bind="$attrs"
    @click:eye="switchField"
    @click:eye-off="switchField"
  >
    <template #label><slot name="label"></slot></template>
    <template #top-right><slot name="top-right"></slot></template>
    <template #buttons><slot name="buttons"></slot></template>
    <template #after>
      <div v-if="strengthVisible" class="input-password__strength">
        <div class="input-password__strength-bar" :style="{ backgroundColor: strengthColor }"></div>
      </div>
    </template>
  </input-text>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

import InputText from "@core/input/InputText.vue";

defineOptions({
  name: "InputPassword",
});

const props = withDefaults(defineProps<{
  value?: string;
  strengthVisible?: boolean;
}>(), {
  strengthVisible: false
});

const inputElement = ref<InstanceType<typeof InputText>>();

const fieldType = ref<"password" | "text">("password");

function switchField() {
  fieldType.value = fieldType.value === "password" ? "text" : "password";
}

const icon = computed(() => {
  if (fieldType.value === "password") {
    return "eye";
  } else {
    return "eye-off";
  }
});

const icons = computed((): ["eye" | "eye-off"] => {
  return [icon.value];
});

function focus() {
  if (inputElement.value) {
    return inputElement.value.focus();
  }
}

const passwordStrength = computed(() => {
  // Very simple password strength calculation
  // Based on length, diversity and variance
  // The score is between 0 and 1
  // Could be improved by adding dictionary checks.

  let passwordCopy = props.value || '';

  const rules = [
    {
      regex: "[a-z]",
      message: 'lowercase'
    },
    {
      regex: '[A-Z]',
      message: 'uppercase'
    },
    {
      regex: '[0-9]',
      message: 'number'
    },
    {
      regex: '[^0-9A-Za-z]',
      message: 'symbol'
    }
  ];

  const diversity = rules
    .filter(rule => new RegExp(`${rule.regex}`).test(passwordCopy))
    .length;

  const length = passwordCopy.length;

  const variance = Array.from(new Set(passwordCopy.split(''))).length;

  const MIN_SECURE_LENGTH = 12;
  const MIN_LENGTH = 4;
  const maxScoreDiversity = 4;
  const maxScoreLength = Math.max(length - MIN_LENGTH, MIN_SECURE_LENGTH - MIN_LENGTH);
  const maxScoreVariance = 7;

  const lengthScore = Math.min(Math.max(length - MIN_LENGTH, 0) / maxScoreLength, 1);
  const diversityScore = Math.min(diversity / maxScoreDiversity, 1);
  const varianceScore = Math.min(variance / maxScoreVariance, 1);

  return Math.max((lengthScore + diversityScore * varianceScore) / 2, 0.025);
});

const strengthColor = computed(() => {
  if (passwordStrength.value < 0.4) {
    return "hsl(0, 100%, 50%)";
  } else if (passwordStrength.value < 0.6) {
    return "hsl(40, 100%, 50%)";
  } else if (passwordStrength.value < 0.8) {
    return "hsl(62, 100%, 35%)";
  } else {
    return "hsl(120, 100%, 35%)";
  }
});

defineExpose({
  focus,
  switchField
});
</script>

<style lang="scss">
.input-password {
  &__strength {
    height: 0.25rem;
    background-color: #e0e0e0;
    overflow: hidden;
  }

  &__strength-bar {
    height: 100%;
    background-color: #4caf50;
    width: calc(100% * v-bind(passwordStrength));
    transition: width 250ms, background-color 250ms;
    z-index: 2;
  }

  &.-with-strength .input {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
}
</style>
